import { useEffect, useRef, useState } from 'react';
import {
  Card, CardContent, Stepper, Step, StepLabel, Button, Stack, TextField, MenuItem, Chip, Alert, Box, Typography,
  IconButton, FormControlLabel, Checkbox, Snackbar, CircularProgress,
} from '@mui/material';
import { Add, Delete, Save, Send, RestartAlt, Close } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/PageHeader';
import { FileUploadField } from '../../../components/FileUploadField';
import { clientOnboardingApi, BILLING_STRUCTURES, type ClientApplication } from '../../../api/clientOnboarding.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const STEPS = ['Company', 'Contacts', 'Billing', 'Accounts', 'Commercial', 'Documents', 'Declaration', 'Review'];
const DOC_TYPES = ['gst_certificate', 'pan_card', 'company_registration', 'cancelled_cheque', 'mou', 'msme', 'company_profile'];
const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', commercial_review: 'Commercial Review',
  approved: 'Approved', active: 'Active', inactive: 'Inactive', rejected: 'Rejected', sent_back: 'Sent Back', clarification_required: 'Clarification Required',
};

type Row = Record<string, unknown>;

function Repeater({ title, rows, setRows, fields, addLabel, readOnly }: { title: string; rows: Row[]; setRows: (r: Row[]) => void; fields: Array<{ key: string; label: string; type?: string; options?: string[] }>; addLabel: string; readOnly: boolean }) {
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      <Stack spacing={1.5}>
        {rows.map((row, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {fields.map((f) => (
              f.type === 'file' ? (
                <Box key={f.key} sx={{ minWidth: 220 }}>
                  <FileUploadField label={f.label} disabled={readOnly} value={(row[f.key] as string) ?? null}
                    onChange={(url) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [f.key]: url ?? '' } : r)))} />
                </Box>
              ) : (
                <TextField key={f.key} size="small" label={f.label} type={f.type ?? 'text'} select={Boolean(f.options)} value={(row[f.key] as string) ?? ''} disabled={readOnly}
                  onChange={(e) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [f.key]: e.target.value } : r)))} sx={{ minWidth: 150 }}>
                  {f.options?.map((o) => <MenuItem key={o} value={o}>{o.replace(/_/g, ' ')}</MenuItem>)}
                </TextField>
              )
            ))}
            {!readOnly && <IconButton size="small" color="error" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}><Delete fontSize="small" /></IconButton>}
          </Stack>
        ))}
      </Stack>
      {!readOnly && <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => setRows([...rows, {}])}>{addLabel}</Button>}
    </Box>
  );
}

export function ClientOnboardingForm() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const queryKey = ['client-application-edit', id];
  const { data, isLoading } = useQuery({ queryKey, queryFn: () => clientOnboardingApi.get(id) });

  const [form, setForm] = useState<Partial<ClientApplication>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => { if (data) setForm(data); }, [data]);

  const { user } = useAuth();
  const isAdmin = (user?.roles ?? []).some((r) => r === 'super_admin' || r === 'admin');
  // Admin / Super Admin may edit at any stage (incl. an active client); others only pre-approval.
  const editable = isAdmin || form.status === 'draft' || form.status === 'sent_back' || form.status === 'clarification_required';
  // Editing an already-activated client → changes are approval-gated; submitting re-runs the workflow.
  const isReapproval = form.status === 'active' || form.status === 'inactive';
  const set = (k: keyof ClientApplication, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  const setChild = (k: keyof ClientApplication, rows: Row[]) => setForm((f) => ({ ...f, [k]: rows }));

  function payload(autosave: boolean): Record<string, unknown> {
    const f = formRef.current;
    const { id: _id, application_code, status, current_stage, client_id, contact_person, contact_email, contact_mobile, workflow, ...rest } = f as Record<string, unknown>;
    void _id; void application_code; void status; void current_stage; void client_id; void contact_person; void contact_email; void contact_mobile; void workflow;
    return { ...rest, __autosave: autosave };
  }

  const saveMut = useMutation({
    mutationFn: (autosave: boolean) => clientOnboardingApi.saveDraft(id, payload(autosave)),
    onSuccess: (res, autosave) => { setForm(res); if (!autosave) setToast('Draft saved'); },
    onError: (e) => setToast(extractApiError(e)),
  });
  const submitMut = useMutation({
    mutationFn: () => clientOnboardingApi.submit(id),
    onSuccess: (res) => { setForm(res); qc.invalidateQueries({ queryKey }); setSubmitted(true); setToast(isReapproval ? 'Re-submitted for re-approval' : 'Submitted for approval'); },
    onError: (e) => setToast(extractApiError(e)),
  });
  const respondMut = useMutation({
    mutationFn: () => clientOnboardingApi.respondClarification(id),
    onSuccess: (res) => { setForm(res); setToast('Re-submitted for review'); },
    onError: (e) => setToast(extractApiError(e)),
  });

  // Autosave only during the normal pre-approval editing window — never auto-touch an
  // approved/active record (admins edit those manually via Save).
  const autosaveOn = form.status === 'draft' || form.status === 'sent_back' || form.status === 'clarification_required';
  useEffect(() => {
    if (!autosaveOn) return undefined;
    const t = setInterval(() => saveMut.mutate(true), 120_000);
    return () => clearInterval(t);
  }, [autosaveOn]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !form.id) return <Box p={4} textAlign="center"><CircularProgress /></Box>;

  if (submitted) {
    return (
      <>
        <PageHeader title="Client Onboarding" subtitle={`Application ${form.application_code}`} />
        <Card><CardContent>
          <Alert severity="success" sx={{ mb: 2 }}>Client application submitted successfully and is now under review.</Alert>
          <Typography variant="subtitle2" gutterBottom>Recommended next actions</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" onClick={() => navigate('/projects')}>Create Project</Button>
            <Button variant="outlined" onClick={() => navigate('/boqs')}>Upload BOQ</Button>
            <Button variant="outlined" onClick={() => navigate('/sites')}>Add Site</Button>
            <Button variant="contained" onClick={() => navigate('/client-applications')}>Go to Applications</Button>
          </Stack>
        </CardContent></Card>
      </>
    );
  }

  const T = (k: keyof ClientApplication, label: string, opts: { type?: string; min?: number } = {}) => (
    <TextField label={label} type={opts.type ?? 'text'} value={(form[k] as string | number) ?? ''} disabled={!editable}
      onChange={(e) => set(k, opts.type === 'number' ? Number(e.target.value) : e.target.value)} sx={{ minWidth: 220 }} />
  );
  const Bool = (k: keyof ClientApplication, label: string) => (
    <FormControlLabel control={<Checkbox checked={Boolean(form[k])} disabled={!editable} onChange={(e) => set(k, e.target.checked)} />} label={label} />
  );

  return (
    <>
      <PageHeader title="Client Onboarding" subtitle={`Application ${form.application_code}`}
        action={<Chip color={form.status === 'active' || form.status === 'approved' ? 'success' : form.status === 'rejected' ? 'error' : 'info'} label={STATUS_LABEL[form.status ?? 'draft'] ?? form.status} />} />

      {!editable && form.status !== 'active' && (
        <Alert severity="info" sx={{ mb: 2 }}>This application is <b>{STATUS_LABEL[form.status ?? ''] ?? form.status}</b>. Editing is locked while under review.</Alert>
      )}
      {editable && isReapproval && (
        <Alert severity="warning" sx={{ mb: 2 }}>This client is already <b>active</b>. Your edits will <b>not</b> go live until you re-submit and the application is approved again (General → Commercial). Existing logins and dealer/product mappings are not affected.</Alert>
      )}
      {form.status === 'clarification_required' && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => respondMut.mutate()}>Re-submit</Button>}>Clarification requested — update and re-submit.</Alert>
      )}

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, flexWrap: 'wrap' }}>
        {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>

      <Card><CardContent>
        <Stack spacing={2}>
          {activeStep === 0 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('company_name', 'Company Name *')}
              {Bool('gst_registered', 'GST Registered?')}
              {T('gst_number', 'Company GST Number')}
              {T('pan_number', 'Company PAN Number')}
              {T('registered_address', 'Registered Office Address')}
              {T('website', 'Company Website')}
              {T('industry_type', 'Industry / Business Type')}
            </Stack>
          )}
          {activeStep === 1 && <Repeater title="Contact Details" rows={(form.contacts ?? []) as unknown as Row[]} setRows={(r) => setChild('contacts', r)} readOnly={!editable} addLabel="Add contact"
            fields={[{ key: 'contact_type', label: 'Type', options: ['primary', 'accounts', 'escalation'] }, { key: 'name', label: 'Name' }, { key: 'designation', label: 'Designation' }, { key: 'department', label: 'Department' }, { key: 'mobile', label: 'Mobile' }, { key: 'alternate_mobile', label: 'Alt Mobile' }, { key: 'email', label: 'Email' }]} />}
          {activeStep === 2 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              <TextField select label="Billing Structure *" value={form.billing_structure ?? ''} disabled={!editable} onChange={(e) => set('billing_structure', e.target.value)} sx={{ minWidth: 220 }}>
                {BILLING_STRUCTURES.map((b) => <MenuItem key={b} value={b}>{b.replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
              {T('billing_company_name', 'Billing Company Name *')}
              <FormControlLabel control={<Checkbox checked={Boolean(form.billing_same_as_registered)} disabled={!editable}
                onChange={(e) => { const c = e.target.checked; setForm((f) => ({ ...f, billing_same_as_registered: c, billing_address: c ? f.registered_address ?? '' : f.billing_address })); }} />} label="Same as Registered Office Address" />
              {T('billing_address', 'Billing Address *')}
              {T('billing_gst_number', 'Billing GST Number')}
              {T('billing_state_id', 'Billing State (ID)', { type: 'number' })}
              {T('billing_city', 'Billing City *')}
              {T('billing_pincode', 'Billing Pin Code *')}
            </Stack>
          )}
          {activeStep === 3 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('accounts_contact_person', 'Accounts Contact Person *')}
              {T('accounts_email', 'Accounts Email ID *')}
              {T('accounts_mobile', 'Accounts Mobile Number *')}
              {T('accounts_alternate_number', 'Accounts Alternate Number')}
              {T('finance_head_name', 'Finance Head Name')}
              {T('finance_head_email', 'Finance Head Email ID')}
              {T('finance_head_mobile', 'Finance Head Mobile Number')}
            </Stack>
          )}
          {activeStep === 4 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('credit_period_days', 'Credit Period (Days)', { type: 'number' })}
              {T('credit_limit', 'Credit Limit', { type: 'number' })}
              {T('payment_terms', 'Payment Terms')}
              {T('invoice_submission_process', 'Invoice Submission Process')}
              {T('billing_frequency', 'Billing Frequency')}
              {Bool('tds_applicable', 'TDS Applicable?')}
              {T('tds_percentage', 'TDS Percentage', { type: 'number' })}
              {Bool('security_deposit_required', 'Security Deposit Required?')}
            </Stack>
          )}
          {activeStep === 5 && (
            <>
              <Alert severity="info">Mandatory: GST Certificate, PAN Card, Company Registration, Cancelled Cheque, MOU/Agreement. Optional: MSME, Company Profile. Record each document below (file upload via the document repository).</Alert>
              <Repeater title="Documents" rows={(form.documents ?? []) as unknown as Row[]} setRows={(r) => setChild('documents', r)} readOnly={!editable} addLabel="Add document"
                fields={[{ key: 'document_type', label: 'Type', options: DOC_TYPES }, { key: 'file_name', label: 'File Name' }, { key: 'file_url', label: 'Document', type: 'file' }]} />
            </>
          )}
          {activeStep === 6 && (
            <Stack spacing={2}>
              <Stack direction="row" flexWrap="wrap" gap={2}>
                {T('declaration_name', 'Declaration Name *')}
                {T('declaration_designation', 'Declaration Designation *')}
                {T('declaration_company_name', 'Declaration Company Name *')}
                {T('declaration_date', 'Declaration Date *', { type: 'date' })}
              </Stack>
              <FormControlLabel control={<Checkbox checked={Boolean(form.declaration_accepted)} disabled={!editable} onChange={(e) => set('declaration_accepted', e.target.checked)} />}
                label="I hereby declare that all information provided above is true and correct to the best of my knowledge and I am authorized to submit this on behalf of the organization." />
            </Stack>
          )}
          {activeStep === 7 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Review &amp; Submit</Typography>
              <Typography variant="body2" color="text.secondary">Company: <b>{form.company_name}</b> · GST: {form.gst_number ?? '—'} · Billing: {form.billing_structure ?? '—'}</Typography>
              <Typography variant="body2" color="text.secondary">Contacts: {(form.contacts ?? []).length} · Documents: {(form.documents ?? []).length}</Typography>
              {!form.declaration_accepted && <Alert severity="warning">The declaration must be accepted before submission.</Alert>}
            </Stack>
          )}
        </Stack>

        <Stack direction="row" justifyContent="space-between" mt={3}>
          <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>Previous</Button>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<Close />} color="inherit" onClick={() => navigate('/client-applications')}>Cancel</Button>
            {editable && <Button startIcon={<RestartAlt />} color="inherit" onClick={() => data && setForm(data)}>Reset</Button>}
            {editable && <Button startIcon={<Save />} variant="outlined" disabled={saveMut.isPending} onClick={() => saveMut.mutate(false)}>Save Draft</Button>}
            {activeStep < STEPS.length - 1 && <Button variant="contained" onClick={() => { if (editable) saveMut.mutate(true); setActiveStep((s) => s + 1); }}>Next</Button>}
            {activeStep === STEPS.length - 1 && editable && <Button startIcon={<Send />} variant="contained" color="success" disabled={submitMut.isPending} onClick={() => submitMut.mutate()}>{isReapproval ? 'Submit for Re-approval' : 'Submit for Approval'}</Button>}
          </Stack>
        </Stack>
      </CardContent></Card>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
